export interface TextLinkSegment {
  text: string;
  href?: string;
}

const LINK_PATTERN = /\b(?:https?:\/\/|www\.)[^\s<>"]+/gi;
const TRAILING_PUNCTUATION = new Set([".", ",", ";", ":", "!", "?", "'", '"']);

function countChar(value: string, char: string): number {
  return value.split(char).length - 1;
}

function trimTrailingPunctuation(candidate: string): string {
  let end = candidate.length;
  while (end > 0) {
    const last = candidate[end - 1];
    if (TRAILING_PUNCTUATION.has(last)) {
      end -= 1;
      continue;
    }
    const head = candidate.slice(0, end);
    if (last === ")" && countChar(head, ")") > countChar(head, "(")) {
      end -= 1;
      continue;
    }
    break;
  }
  return candidate.slice(0, end);
}

/**
 * Splits free text into plain and link segments so URLs can be rendered as anchors.
 * Only http(s) and www-prefixed URLs become links; other schemes stay plain text.
 */
export function splitTextLinks(text: string): TextLinkSegment[] {
  if (!text) {
    return [];
  }

  const segments: TextLinkSegment[] = [];
  let lastIndex = 0;

  for (const match of text.matchAll(LINK_PATTERN)) {
    const url = trimTrailingPunctuation(match[0]);
    const start = match.index ?? 0;
    if (url === "" || /^www\.$/i.test(url)) {
      continue;
    }
    if (start > lastIndex) {
      segments.push({ text: text.slice(lastIndex, start) });
    }
    const href = /^www\./i.test(url) ? `https://${url}` : url;
    segments.push({ text: url, href });
    lastIndex = start + url.length;
  }

  if (lastIndex < text.length) {
    segments.push({ text: text.slice(lastIndex) });
  }

  return segments;
}
